// src/stores/boot.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import app from 'configs/appResolver';
import { useThemeStore } from 'stores/theme';

import { logErrorSilent } from 'utils/errorLogger';
// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const LOCAL_STORAGE_KEY = 'storage-boot';
const BOOT_OK_DELAY_MS = 800;
const LOADING_DELAY = 0;
const removeLoading = false;
const showLoadingConsole = false;

// *******************************************************************************
// STATE
// *******************************************************************************
export const initialState = {
  // boot lifecycle
  isLoggingOut: false,
  firstBootActive: true,
  requestsInFlight: 0,
  requestsErrorFlag: false,
  bootTasks: new Set(),

  // global loading configs
  loadingApp: {
    visible: !!!removeLoading,
    icon: null,
    title: 'loading_application',
    translucid: false,
    extendedDelayToHide: 0,
  },
  loadingModule: {
    visible: false,
    icon: null,
    title: 'loading_module',
    translucid: false,
    extendedDelayToHide: 0,
  },
  loadingPage: {
    visible: false,
    icon: null,
    title: 'loading_page',
    translucid: false,
    extendedDelayToHide: 0,
    showedAt: null, // timestamp when loading was shown (for minimum display time)
  },

  loadingAreas: {
    // multiple area overlays; __default é aplicado quando name não é informado
    __default: {
      visible: false,
      icon: null,
      title: 'loading_data',
      translucid: true,
      extendedDelayToHide: 0,
      showProgress: true,
    },

    // multiple area overlays; __all é aplicado em todos independentemente se name é informado ou não
    __all: {
      visible: false,
      icon: null,
      title: 'loading_data',
      translucid: true,
      extendedDelayToHide: 0,
      showProgress: true,
    },

    // multiple area overlays; <SimpleLoadingArea name> é aplicado em todos identificados com este name
    cardHeader: {
      visible: false,
      icon: null,
      title: 'loading_data',
      translucid: true,
      extendedDelayToHide: 0,
      showProgress: true,
    },
  },

  // derived
  isBootReady: () => false,
};

// *******************************************************************************
// METHODS
// *******************************************************************************
function buildStorageKey() {
  return `${app.ID}:${LOCAL_STORAGE_KEY}`;
}

export function getStorageBootItem(key) {
  const field = buildStorageKey();
  let lsData;
  try {
    lsData = JSON.parse(localStorage.getItem(field));
  } catch (e) {
    logErrorSilent(e, {
      context: 'bootStore.getStorageBootItem (JSON.parse)',
      extraData: { field, key },
    });
  }
  return lsData?.[key];
}

export function setStorageBootItem(key, value) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (value) {
    lsData[key] = value;
  } else {
    delete lsData[key];
  }
  localStorage.setItem(field, JSON.stringify(lsData));
}

export function removeStorageBootItem(key) {
  const field = buildStorageKey();
  let lsData = {};
  try {
    lsData = JSON.parse(localStorage.getItem(field)) || {};
  } catch (e) {
    lsData = {};
  }
  if (lsData.hasOwnProperty(key)) {
    delete lsData[key];
    localStorage.setItem(field, JSON.stringify(lsData));
  }
}

// *******************************************************************************
// STORE
// *******************************************************************************
const store = create(
  devtools(
    (set, get) => ({
      ...initialState,

      // 1) DERIVED
      isBootReady: () => {
        const s = get();
        const noTasks = s.bootTasks.size === 0;
        return noTasks && s.requestsInFlight === 0 && !s.requestsErrorFlag;
      },

      setIsLoggingOut: v => set({ isLoggingOut: !!v }),

      // 2) BOOT TASKS
      beginBootTask: id => {
        if (!id) return;
        set(state => {
          const tasks = new Set(state.bootTasks);
          tasks.add(id);
          return { bootTasks: tasks };
        });
      },

      endBootTask: (id, ok = true) => {
        if (!id) return;
        set(state => {
          const tasks = new Set(state.bootTasks);
          tasks.delete(id);
          return { bootTasks: tasks, requestsErrorFlag: ok ? state.requestsErrorFlag : true };
        });
      },

      // 3) REQUEST COUNTERS
      incRequests: () => set(state => ({ requestsInFlight: state.requestsInFlight + 1 })),
      decRequests: (ok = true) =>
        set(state => ({
          requestsInFlight: Math.max(0, state.requestsInFlight - 1),
          requestsErrorFlag: ok ? state.requestsErrorFlag : true,
        })),

      // 4) FIRST BOOT LIFECYCLE
      finishFirstBoot: () => set({ firstBootActive: false }),
      tryFinishFirstBootSoon: () => {
        const s = get();
        if (!s.firstBootActive) return;
        if (s.isBootReady()) {
          setTimeout(() => {
            if (get().isBootReady()) get().finishFirstBoot();
          }, BOOT_OK_DELAY_MS);
        }
      },
      resetFirstBoot: () =>
        set({
          firstBootActive: true,
          requestsInFlight: 0,
          requestsErrorFlag: false,
          bootTasks: new Set(),
        }),

      // 5) LOADING CONFIGS
      setLoadingApp: payload => {
        if (removeLoading) return;
        const { loadingApp } = get();
        if (typeof payload === 'boolean') payload = { visible: payload };

        let { visible, extendedDelayToHide, ...rest } = payload || {};

        const payloadDelay = extendedDelayToHide; // payloadExtendedDelayToHide
        const defaultDelay = LOADING_DELAY + 400; // defaultExtendedDelayToHide
        const sessionDelay = loadingApp?.extendedDelayToHide || 0; // sessionExtendedDelayToHide

        if (showLoadingConsole) {
          console.warn('setLoadingApp:', payload.visible, {
            defaultDelay,
            sessionDelay,
            payloadDelay,
            payload,
          });
        }

        if (!visible) rest.title = initialState.loadingApp.title;

        const tmrDelay = !visible ? payloadDelay || sessionDelay || defaultDelay || 0 : 0;

        schedule(tmrDelay, () =>
          set(state => ({
            loadingApp: {
              ...initialState.loadingApp,
              ...rest,
              visible,
              extendedDelayToHide: visible ? payloadDelay || defaultDelay || 0 : 0,
            },
          }))
        );
      },

      setLoadingModule: payload => {
        if (removeLoading) return;
        // função cancelada temporariamente, não remover a linha de baixo
        // if (!forceShow) return;

        const { loadingApp, loadingModule } = get();
        if (typeof payload === 'boolean') payload = { visible: payload };

        let { visible, extendedDelayToHide, ...rest } = payload || {};

        if (visible && loadingApp.visible) return;

        const payloadDelay = extendedDelayToHide; // payloadExtendedDelayToHide
        const defaultDelay = LOADING_DELAY + 300; // defaultExtendedDelayToHide
        const sessionDelay = loadingModule?.extendedDelayToHide || 0; // sessionExtendedDelayToHide

        if (showLoadingConsole) {
          console.warn('setLoadingModule:', payload.visible, {
            defaultDelay,
            sessionDelay,
            payloadDelay,
            payload,
          });
        }

        if (!visible) rest.title = initialState.loadingModule.title;

        const tmrDelay = !visible ? payloadDelay || sessionDelay || defaultDelay || 0 : 0;

        schedule(tmrDelay, () =>
          set(state => ({
            loadingModule: {
              ...initialState.loadingModule,
              ...rest,
              visible,
              extendedDelayToHide: visible ? payloadDelay || defaultDelay || 0 : 0,
            },
          }))
        );
      },

      setLoadingPage: payload => {
        // if (!payload) return;
        if (removeLoading) return;
        const { loadingApp, loadingModule, loadingPage } = get();
        if (typeof payload === 'boolean') payload = { visible: payload };

        let { visible, extendedDelayToHide, ...rest } = payload || {};

        if (visible && (loadingApp.visible || loadingModule.visible)) return;
        if (visible === loadingPage.visible) return;

        const payloadDelay = extendedDelayToHide; // payloadExtendedDelayToHide
        const defaultDelay = LOADING_DELAY + 600; // defaultExtendedDelayToHide
        const sessionDelay = loadingPage?.extendedDelayToHide || 0; // sessionExtendedDelayToHide
        const minDisplayTime = 200; // minimum time to show loading (ms)

        if (showLoadingConsole) {
          console.warn('setLoadingPage:', payload.visible, {
            defaultDelay,
            sessionDelay,
            payloadDelay,
            payload,
          });
        }

        if (!visible) rest.title = initialState.loadingPage.title;

        // Calculate delay for hiding
        let tmrDelay = 0;
        if (!visible) {
          // Calculate how long the loading has been visible
          const showedAt = loadingPage?.showedAt;
          const elapsedTime = showedAt ? Date.now() - showedAt : minDisplayTime;
          const remainingMinTime = Math.max(0, minDisplayTime - elapsedTime);

          // Use the greater of: remaining minimum time OR configured delay
          tmrDelay = Math.max(remainingMinTime, payloadDelay || sessionDelay || defaultDelay || 0);
        }

        schedule(tmrDelay, () =>
          set(() => ({
            loadingPage: {
              ...initialState.loadingPage,
              ...rest,
              visible,
              extendedDelayToHide: visible ? payloadDelay || defaultDelay || 0 : 0,
              showedAt: visible ? Date.now() : null, // save timestamp when showing
            },
          }))
        );
      },

      setLoadingArea: payload => {
        if (removeLoading) return;

        const { loadingApp, loadingModule, loadingPage, loadingAreas } = get();

        if (typeof payload === 'boolean') payload = { visible: payload };

        let { visible, extendedDelayToHide, ...rest } = payload || {};

        if (visible && (loadingApp.visible || loadingModule.visible || loadingPage.visible)) return;

        // Determina qual área está sendo afetada para pegar o sessionDelay correto
        const targetArea = name
          ? (loadingAreas[name] ?? loadingAreas.__default)
          : loadingAreas.__default;

        const payloadDelay = extendedDelayToHide; // payloadExtendedDelayToHide
        const defaultDelay = LOADING_DELAY + 100; // defaultExtendedDelayToHide
        const sessionDelay = targetArea?.extendedDelayToHide || 0; // sessionExtendedDelayToHide

        if (showLoadingConsole) {
          console.warn('setLoadingArea:', payload.visible, {
            defaultDelay,
            sessionDelay,
            payloadDelay,
            payload,
          });
        }

        if (!visible)
          rest.title =
            initialState.loadingAreas.__default?.title || initialState.loadingAreas.title;

        const tmrDelay = !visible ? payloadDelay || sessionDelay || defaultDelay || 0 : 0;

        const apply = updater =>
          set(state => {
            const next = { ...state.loadingAreas };

            if (name) {
              const base = next[name] ?? state.loadingAreas.__default ?? {};
              next[name] = { ...base, ...updater(base) };
            } else {
              // sem name => aplica para __default e __all
              if (next.__default)
                next.__default = { ...next.__default, ...updater(next.__default) };
              if (next.__all) next.__all = { ...next.__all, ...updater(next.__all) };
            }

            return { loadingAreas: next };
          });

        if (typeof visible === 'boolean') {
          schedule(tmrDelay, () =>
            apply(prev => ({
              ...(prev || {}),
              visible,
              extendedDelayToHide: visible ? payloadDelay || defaultDelay || 0 : 0,
            }))
          );
        }

        if (Object.keys(rest).length) {
          apply(prev => ({ ...(prev || {}), ...rest }));
        }
      },
    }),
    { name: 'bootStore', enabled: true }
  )
);

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export const useBootStore = store;
// export const bootStore = store.getState;
// export const fromBootStore = sel => sel(bootStore());

// *******************************************************************************
// SELECTORS (For non-React contexts)
// *******************************************************************************
export const fromBootStore = selector => {
  if (typeof selector === 'function') {
    return selector(useBootStore.getState());
  }
  return useBootStore.getState();
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
function schedule(ms, fn) {
  if (ms > 0) setTimeout(fn, ms);
  else fn();
}
